import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX, ArrowRight, RotateCcw, Loader2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import BodyCheckCard from "@/components/app/BodyCheckCard";
import { api } from "@/lib/auth";
import { ANKH_PATHS, ANKH_JEWEL } from "@/lib/ankh";
import { playVoice, stopVoice } from "@/lib/ariel";

// Ankhing the Breath, following Gate 5 Breathwave's Ankhing Breath Protocol (text served to initiates by the API).
const VOICE_KEY = "tt-breath-voice";
const GLOW = { filter: "drop-shadow(0 0 4px rgba(244,217,164,0.85))" };
const LOOP = "M0 -7 C -13 -16 -16 -47 0 -48 C 16 -47 13 -16 0 -7";
const CIRCUIT = `M0 46 L0 -7 C -13 -16 -16 -47 0 -48 C 16 -47 13 -16 0 -7 L0 46`;
const RUNS = {
  1: { rounds: 9, phases: [["in", "Inhale through the nose", "Perineum rises, energy climbs the back spine to the back heart", 4000, "Inhale. Rise up the back spine."], ["hold", "Hold", "Feel the back heart expand", 2000, "Hold. The back heart expands."], ["out", "Exhale · “HAH”", "Arc over the crown, through the front heart, home to the perineum", 6000, "Exhale, hah. Over the crown, through the heart, home."]] },
  2: { rounds: 9, phases: [["flow", "Let the circuit flow", "Perineum · back spine · back heart · crown · front heart · perineum", 6000, "Let the circuit flow, without interruption."]], quicken: 0.88 },
  3: { rounds: 9, phases: [["circle", "Circle the crown", "Back heart · top of head · front heart, clockwise", 4000, "Circle the breath around the head."]] },
};
const say = (on, text) => (on ? playVoice(text).catch(() => {}) : Promise.resolve());
const paras = (part) => part.items.map((it) => (it.k === "label" ? `${it.t}` : it.t)).join(" ");

export const LivingAnkh = ({ phase, beat, ms = 4000, className = "w-56 h-56 sm:w-64 sm:h-64", testId = "breath-ankh" }) => {
  const d = ms / 1000;
  return (
    <svg data-testid={testId} data-phase={phase ?? "rest"} viewBox="-55 -62 110 112" className={`${className} overflow-visible`} aria-hidden="true">
      <g fill="none" stroke="#D9B47A" strokeOpacity="0.28" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
        {ANKH_PATHS.map((p) => <path key={p} d={p} />)}
      </g>
      <g fill="none" stroke="#F4D9A4" strokeWidth="2.6" strokeLinecap="round" style={GLOW}>
        {phase === "in" && <motion.path key={`in-${beat}`} d="M0 46 L0 -4" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: d, ease: "easeInOut" }} />}
        {phase === "out" && <motion.path key={`out-${beat}`} d={`${LOOP} L0 46`} initial={{ pathLength: 0, opacity: 1 }} animate={{ pathLength: 1, opacity: [1, 1, 0.3] }} transition={{ duration: d, ease: "easeInOut" }} />}
        {phase === "flow" && <motion.path key={`flow-${beat}`} d={CIRCUIT} initial={{ pathLength: 0, pathOffset: 0 }} animate={{ pathLength: [0, 0.35, 0.35, 0], pathOffset: [0, 0.2, 0.65, 1] }} transition={{ duration: d, ease: "linear" }} />}
        {phase === "circle" && <motion.path key={`circle-${beat}`} d={LOOP} initial={{ pathLength: 0.2, pathOffset: 0 }} animate={{ pathOffset: [0, 0.8] }} transition={{ duration: d, ease: "linear" }} />}
      </g>
      {(phase === "hold" || phase === "in") && <motion.circle key={`heart-${beat}`} cx="0" cy="-4" r="12" fill="#F4D9A4" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: phase === "hold" ? 0.3 : 0.12, scale: phase === "hold" ? 1.2 : 0.8 }} transition={{ duration: phase === "hold" ? d : d * 0.8 }} style={{ transformBox: "fill-box", originX: "50%", originY: "50%" }} />}
      {phase === "fire" && Array.from({ length: 12 }, (_, k) => {
        const a = (k / 12) * Math.PI * 2;
        return <motion.line key={`f-${beat}-${k}`} x1="0" y1="-48" x2={Math.cos(a) * 20} y2={-48 + Math.sin(a) * 20} stroke={k % 2 ? "#E8838E" : "#F4D9A4"} strokeWidth="2" strokeLinecap="round" style={GLOW}
          initial={{ pathLength: 0, opacity: 1 }} animate={{ pathLength: [0, 1, 1], opacity: [1, 1, 0] }} transition={{ duration: 1.6, delay: (k % 3) * 0.08, repeat: Infinity, repeatDelay: 0.6 }} />;
      })}
      <circle {...ANKH_JEWEL} fill="#C2414F" />
    </svg>
  );
};

const Guided = ({ run, voice, onDone }) => {
  const cfg = RUNS[run];
  const [round, setRound] = useState(1);
  const [idx, setIdx] = useState(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const [label, sub, base, cue] = cfg.phases[idx].slice(1);
  const ms = Math.round(base * (cfg.quicken ? cfg.quicken ** (round - 1) : 1));
  useEffect(() => {
    if (voice && round <= 2) say(true, cue);
    const t = setTimeout(() => {
      if (idx < cfg.phases.length - 1) setIdx(idx + 1);
      else if (round < cfg.rounds) {
        setRound(round + 1);
        setIdx(0);
      } else doneRef.current();
    }, ms);
    return () => clearTimeout(t);
  }, [idx, round, ms, cue, voice, cfg]);
  return (
    <div data-testid="breath-cycle" className="flex flex-col items-center text-center">
      <LivingAnkh phase={cfg.phases[idx][0]} beat={`${round}-${idx}`} ms={ms} />
      <AnimatePresence initial={false}>
        <motion.p key={`${round}-${idx}`} data-testid="breath-phase" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8 font-display text-3xl sm:text-4xl">{label}</motion.p>
      </AnimatePresence>
      <p className="mt-2 text-[15px] text-[#A8998C] max-w-sm">{sub}</p>
      <p data-testid="breath-round" className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">{run === 3 ? "Circle" : "Round"} {round} of {cfg.rounds}</p>
      <button data-testid="breath-skip" onClick={() => doneRef.current()} className="mt-8 text-sm text-[#A8998C] underline underline-offset-4">Move on when you are ready</button>
    </div>
  );
};

const Text = ({ part }) => (
  <div className="mt-5 space-y-4">
    {part.items.map((it, k) => (it.k === "label" ? <p key={k} className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#E8838E] pt-2">{it.t}</p> : <p key={k} className="text-[17px] text-[#CFC2B6] leading-relaxed">{it.t}</p>))}
  </div>
);

const PATHS = [["non", "Non-ejaculatory path"], ["ejac", "Ejaculatory path"]];

export default function AnkhBreath() {
  const [proto, setProto] = useState(null);
  const [stage, setStage] = useState("intro");
  const [step, setStep] = useState(0);
  const [guiding, setGuiding] = useState(false);
  const [path, setPath] = useState(null);
  const [voice, setVoice] = useState(() => localStorage.getItem(VOICE_KEY) !== "0");
  const [sessions, setSessions] = useState(null);
  useEffect(() => {
    api.get("/practice/ankhing").then(({ data }) => Array.isArray(data?.parts) && setProto(data)).catch(() => {});
    api.get("/practice/breath").then(({ data }) => setSessions(data?.sessions ?? 0)).catch(() => {});
    return () => stopVoice();
  }, []);
  const part = proto?.parts?.[step];
  useEffect(() => {
    if (stage === "steps" && part && !guiding) say(voice, `${part.title}. ${paras(part)}`);
    if (stage === "seal" && proto) say(voice, proto.blessing.join(" "));
  }, [stage, step, part, guiding, voice, proto]);
  const toggleVoice = () => {
    const v = !voice;
    setVoice(v);
    localStorage.setItem(VOICE_KEY, v ? "1" : "0");
    if (!v) stopVoice();
  };
  const go = (n) => {
    stopVoice();
    setGuiding(false);
    setPath(null);
    setStep(n);
  };
  const seal = async () => {
    stopVoice();
    setStage("seal");
    try {
      const { data } = await api.post("/practice/breath", { rounds: 9 });
      setSessions(data?.sessions ?? null);
    } catch {
      // the practice still completes even if it can't be logged
    }
  };
  const last = proto ? proto.parts.length - 1 : 0;
  const cta = "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors";
  const ghost = "w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#D9B47A]/40 text-[#D9B47A] px-6 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors";
  return (
    <main data-testid="ankh-breath" className="max-w-xl mx-auto px-5 pt-[max(2.5rem,calc(env(safe-area-inset-top)+1.5rem))] pb-32 min-h-[100dvh]">
      <div className="flex items-center justify-between">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">Gate 05 · Breathwave</p>
        <button data-testid="breath-voice-toggle" role="switch" aria-checked={voice} onClick={toggleVoice} aria-label="Ariel’s voice" className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${voice ? "border-[#D9B47A] text-[#D9B47A]" : "border-[#D9B47A]/30 text-[#8F8176]"}`}>
          {voice ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>
      {stage === "intro" && (
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="breath-intro">
          <h1 className="mt-6 font-display text-5xl leading-[1.05]">Ankhing the <em className="text-[#D9B47A]">Breath</em></h1>
          <p className="mt-5 text-[16px] text-[#CFC2B6] leading-relaxed">Goddess Ariel’s signature breath. As breath rises through the spine, arcs through the crown and returns through the heart, the body becomes a living circuit of energy, so pleasure circulates rather than collapses.</p>
          <div className="mt-8 flex justify-center"><LivingAnkh phase={null} beat="intro" /></div>
          <BodyCheckCard app="gates" practice="Ankhing the Breath" stage="before" testId="breath-body-before" />
          <button data-testid="breath-begin" disabled={!proto} onClick={() => { go(0); setStage("steps"); }} className={`${cta} mt-10 disabled:opacity-60`}>
            {!proto && <Loader2 className="w-4 h-4 animate-spin" />} Begin the Ankhing Breath <ArrowRight className="w-4 h-4" />
          </button>
          {sessions > 0 && <p data-testid="breath-sessions" className="mt-4 text-center text-sm text-[#A8998C]">You have breathed the Ankh {sessions} {sessions === 1 ? "time" : "times"}.</p>}
          <Link to="/app/breath/partner" data-testid="breath-partner-link" className="group mt-8 flex items-center gap-4 border border-[#E8838E]/30 bg-[#140910] p-5 hover:border-[#E8838E]/70 transition-colors">
            <Users className="w-6 h-6 shrink-0 text-[#E8838E]" strokeWidth={1.5} />
            <span className="min-w-0">
              <span className="block font-display text-xl">Breathe with a partner</span>
              <span className="block text-[13px] text-[#A8998C]">Two ankhs in one rhythm, side by side or across any distance</span>
            </span>
            <ArrowRight className="ml-auto w-4 h-4 shrink-0 text-[#E8838E] transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </motion.section>
      )}
      {stage === "steps" && part && (
        <section data-testid="breath-guide" className="mt-8">
          <div className="flex gap-1.5">{proto.parts.map((_, k) => <span key={k} className={`h-1 flex-1 rounded-full ${k <= step ? "bg-[#D9B47A]" : "bg-[#D9B47A]/15"}`} />)}</div>
          {guiding && RUNS[step] ? (
            <div className="mt-10"><Guided key={step} run={step} voice={voice} onDone={() => go(Math.min(step + 1, last))} /></div>
          ) : (
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <h2 data-testid="breath-step-title" className="mt-10 font-display text-4xl leading-tight">{part.title}</h2>
              {step === last ? (
                <>
                  <p className="mt-5 text-[17px] text-[#CFC2B6] leading-relaxed">{part.items.find((it) => it.k === "p")?.t}</p>
                  <div className="mt-6 grid grid-cols-2 gap-2">
                    {PATHS.map(([k, l]) => <button key={k} data-testid={`breath-path-${k}`} onClick={() => { setPath(k); say(voice, part.items[part.items.findIndex((it) => it.t.startsWith(l)) + 1]?.t ?? ""); }} className={`rounded-full border px-3 py-3 text-[13px] transition-colors ${path === k ? "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]" : "border-[#D9B47A]/30 text-[#CFC2B6]"}`}>{l}</button>)}
                  </div>
                  {path && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="breath-fireworks" className="mt-8 flex flex-col items-center text-center">
                      <LivingAnkh phase="fire" beat={path} />
                      <p className="mt-6 text-[17px] text-[#CFC2B6] leading-relaxed">{part.items[part.items.findIndex((it) => it.t.startsWith(PATHS.find((p) => p[0] === path)[1])) + 1]?.t}</p>
                      <p className="mt-4 font-display italic text-xl text-[#D9B47A]">{part.items[part.items.length - 1]?.t}</p>
                    </motion.div>
                  )}
                  <div className="mt-10 grid gap-3">
                    <button data-testid="breath-return" onClick={() => { go(2); setGuiding(true); }} className={ghost}><RotateCcw className="w-4 h-4" /> Return to the full circuit</button>
                    <button data-testid="breath-seal-btn" onClick={seal} className={cta}>Seal the practice</button>
                  </div>
                </>
              ) : (
                <>
                  <Text part={part} />
                  <button data-testid="breath-next" onClick={() => (RUNS[step] ? (stopVoice(), setGuiding(true)) : go(step + 1))} className={`${cta} mt-10`}>
                    {RUNS[step] ? `Breathe it with Ariel · ${RUNS[step].rounds} ${step === 3 ? "circles" : "rounds"}` : "I am ready"} <ArrowRight className="w-4 h-4" />
                  </button>
                  {RUNS[step] && <button data-testid="breath-skip-step" onClick={() => go(step + 1)} className="mt-4 w-full text-center text-sm text-[#A8998C] underline underline-offset-4">Skip to the next step</button>}
                </>
              )}
            </motion.div>
          )}
        </section>
      )}
      {stage === "seal" && (
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} data-testid="breath-seal" className="mt-10 text-center">
          <div className="flex justify-center"><LivingAnkh phase="hold" beat="seal" /></div>
          {proto?.blessing.map((b, k) => <p key={k} className={`mt-6 ${k ? "text-[16px] text-[#CFC2B6]" : "font-display text-2xl leading-snug"}`}>{b}</p>)}
          {sessions > 0 && <p className="mt-4 text-sm text-[#A8998C]">You have breathed the Ankh {sessions} {sessions === 1 ? "time" : "times"}.</p>}
          <BodyCheckCard app="gates" practice="Ankhing the Breath" stage="after" testId="breath-body-after" />
          <button data-testid="breath-again" onClick={() => { stopVoice(); setStage("intro"); }} className="mt-8 inline-flex items-center gap-2 text-[#D9B47A] underline underline-offset-4"><RotateCcw className="w-4 h-4" /> Breathe again</button>
        </motion.section>
      )}
    </main>
  );
}
